"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { zodResolver } from "@hookform/resolvers/zod";
import { ROUTES } from "@unreserved/constants";
import { useToast } from "@unreserved/hooks";
import { useForgotPassword } from "@unreserved/hooks/use-auth";
import {
  type ForgotPasswordFormData,
  forgotPasswordSchema,
} from "@unreserved/schemas/auth-schemas";
import { handleHttpError } from "@unreserved/utils";
import { useForm } from "react-hook-form";

import { AppButton, FormInput } from "@/app/_components/shared";
import { IMAGES } from "@/app/_libs/constants/images";

export const ForgotPasswordForm = () => {
  const { forgotPasswordMutation } = useForgotPassword();
  const { toast } = useToast();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (_data: ForgotPasswordFormData) => {
    await forgotPasswordMutation.mutateAsync(
      {
        email: _data.email,
      },
      {
        onSuccess: () => {
          toast.success(forgotPasswordMutation.data?.message ?? "Forgot password successful");
          router.push(ROUTES.AUTH.CHECK_EMAIL);
        },
        onError: (error: Error) => {
          handleHttpError(error, "Forgot password failed", toast);
        },
      },
    );
  };

  return (
    <div className="bg-background-0 w-full px-4 pt-6 lg:max-w-[40vw] lg:rounded-lg lg:p-10.5 lg:shadow-[0px_4px_16px_0px_rgba(38,38,38,0.12)]">
      <div className="mb-10.5">
        <div className="mb-2 h-8 lg:h-13">
          <Image
            src={IMAGES.LOGO}
            alt="Unreserved Logo"
            width={130}
            height={32}
            className="lg:h-8 lg:w-44.75"
            objectFit="contain"
          />
        </div>
        <h1 className="text-tertiary-950 mb-1 text-xl font-semibold lg:mb-2 lg:text-4xl">
          Forgot Password
        </h1>
        <p className="text-typography-600 text-xs leading-normal lg:text-sm">
          Enter registered email address to receive reset password link
        </p>
      </div>

      <form id="forgot-password-form" onSubmit={handleSubmit(onSubmit)} className="mb-6 space-y-6">
        <FormInput
          label="Email Address"
          type="email"
          placeholder="Enter your registered email address"
          error={errors.email?.message}
          disabled={isSubmitting}
          onKeyDown={(e) => {
            if (e.key === " ") {
              e.preventDefault();
            }
          }}
          {...register("email")}
        />
      </form>

      <AppButton
        type="submit"
        form="forgot-password-form"
        className="bg-primary-700 h-10 w-full rounded text-sm lg:h-12 lg:rounded-lg lg:text-lg"
        text="Submit"
        isLoading={isSubmitting}
      />

      <p className="text-typography-700 mt-6 text-center text-xs lg:mt-6 lg:text-sm">
        <span className="font-medium">Back to login?</span>{" "}
        <Link href={ROUTES.AUTH.LOGIN} className="text-primary-700 font-semibold hover:underline">
          Login
        </Link>
      </p>
    </div>
  );
};
